<template>
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_GENERAL_LEDGER')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="cachedGeneralLedgersList ? cachedGeneralLedgersList.data : generalLedgersList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_GENERAL_LEDGER')"
              @click="onSelectGeneralLedgerItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectGeneralLedgerItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="fetchGLAccountBalance(data.item, $event.target)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View Balance') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="
                getUserPermission(
                  data.item.status === 'Active'
                    ? 'DEACTIVATE_GENERAL_LEDGER'
                    : 'ACTIVATE_GENERAL_LEDGER'
                )
              "
              @click="glStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === 'Active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      no-close-on-backdrop
      :title="modalTitle"
      modal-footer
      :busy="isBusy"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="glRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- GL Name -->
            <b-col md="6">
              <InputField
                id="gl-name"
                v-model="glFormData.name"
                :label="$t('GL Name')"
                :placeholder="$t('Enter GL Name')"
                type="text"
                :error-name="$t('GL Name')"
                rules="required"
                for="gl-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- GL Name -->

            <!-- Level 1 Category -->
            <b-col md="6">
              <SelectField
                id="level1-category"
                v-model="glFormData.parentCategory"
                :label="$t('Level 1 Category')"
                :placeholder="$t('Select')"
                :options="level1CategoryOptions"
                :reduce="option => option.value"
                :error-name="$t('Level 1 Category')"
                rules="required"
                item="label"
                :value="glFormData.parentCategory"
                :set-selected="fetchLevelTwoCategory"
                :readonly="isEditable"
              />
            </b-col>
            <!-- Level 1 Category -->

            <!-- Level 2 Category -->
            <b-col
              v-if="isView"
              md="6"
            >
              <InputField
                id="gl-name"
                v-model="glFormData.level2Category"
                :label="$t('Level 2 Category')"
                type="text"
                for="level2-category"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col
              v-if="!isView"
              md="6"
            >
              <SelectField
                id="level2-category"
                v-model="glFormData.level2Category"
                :label="$t('Level 2 Category')"
                :placeholder="$t('Select')"
                :options="level2CategoryOptions"
                :reduce="option => option.value"
                :error-name="$t('Level 2 Category')"
                rules="required"
                item="label"
                :set-selected="fetchLevelThreeCategory"
                :readonly="isEditable"
                :loading="isLoadingLevel2Category"
              />
            </b-col>
            <!-- Level 3 Category -->
            <b-col
              v-if="isView"
              md="6"
            >
              <InputField
                id="gl-name"
                v-model="glFormData.accountCategoryCode"
                :label="$t('Level 3 Category')"
                type="text"
                for="gl-name"
                :readonly="isReadonly"
              />
            </b-col>

            <!-- Level 3 Category -->
            <b-col
              v-if="!isView"
              md="6"
            >
              <SelectField
                id="level3-category"
                v-model="glFormData.accountCategoryCode"
                :label="$t('Level 3 Category')"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :options="level3CategoryOptions"
                :error-name="$t('Level 3 Category')"
                rules="required"
                item="label"
                :readonly="isEditable"
                :loading="isLoadingLevel3Category"
              />
            </b-col>
            <!-- Level 3 Category -->

            <!-- IFRS Category -->
            <!-- <b-col
              v-if="isView"
              md="6"
            >
              <InputField
                id="gl-name"
                v-model="glFormData.accountCategoryCode"
                label="IFRS Category"
                type="text"
                for="gl-name"
                :readonly="isReadonly"
              />
            </b-col> -->

            <b-col md="6">
              <SelectField
                id="ifrs-category"
                v-model="glFormData.ifrsAccountMappingID"
                :label="$t('IFRS Category')"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :options="ifrsCategoryOptions"
                :error-name="$t('IFRS Category')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :loading="isLoadingIFRSCategory"
              />
            </b-col>
            <!-- IFRS Category -->

            <!-- Financial Institution -->
            <b-col
              v-if="showFinancialInstitution"
              md="6"
            >
              <SelectField
                id="financial-institution"
                v-model="glFormData.bankCode"
                label="Financial Institution"
                :options="bankOptions"
                :reduce="option => option.value"
                :error-name="$t('Financial Institution')"
                rules="required"
                item="label"
                :placeholder="$t('Select')"
                :readonly="isReadonly"
                :loading="isLoadingBanks"
              />
            </b-col>
            <!-- Financial Institution -->

            <!-- Transaction Permission -->
            <b-col md="6">
              <SelectField
                id="transaction-permission"
                v-model="glFormData.transactionPermission"
                :label="$t('Transaction Permission')"
                :placeholder="$t('Select')"
                :reduce="option => option.value"
                :options="transactionPermissionOptions"
                :error-name="$t('Transaction Permission')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Transaction Permission -->
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="glFormData.currencyCode"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions"
                item="label"
                :placeholder="$t('Select currency')"
                :readonly="isEditable"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- GL Balance modal -->
    <b-modal
      id="view-balance"
      ref="my-modal"
      :ok-title="$t('Close')"
      no-close-on-backdrop
      ok-only
      ok-variant="outline-secondary"
      centered
      size="md"
      :title="$t('General Ledger Balance')"
    >
      <validation-observer ref="otherBusinessRef">
        <b-form>
          <b-row class="match-height">
            <!-- Account Type -->
            <b-col md="12">
              <InputField
                id="title"
                v-model="customerInformation.accountNumber"
                :label="$t('Account Number')"
                type="text"
                for="last-name"
                readonly
              />
            </b-col>
            <!-- Account Type -->

            <!-- Account -->
            <b-col md="12">
              <InputField
                id="ledger-balance"
                v-model="customerInformation.ledgerBalance"
                :label="$t('Ledger Balance')"
                type="text"
                for="last-name"
                readonly
              />
            </b-col>
            <!-- Account -->
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchGeneralLedger"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Account Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Account Class -->
        <b-col md="6">
          <SelectField
            id="level1-category"
            v-model="filter.parentCategory"
            :label="$t('Account Class')"
            :placeholder="$t('Select')"
            item="label"
            :options="level1CategoryOptions"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Account Class -->

        <!-- Account Category -->
        <b-col md="6">
          <SelectField
            id="account-category"
            v-model="filter.accountCategoryCode"
            :label="$t('Parent Name')"
            :placeholder="$t('Select')"
            :options="categoryOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Account Category -->

        <!-- Transaction Permission -->
        <b-col md="6">
          <SelectField
            id="transaction-permission"
            v-model="filter.transactionPermission"
            :label="$t('Transaction Permission')"
            item="label"
            :placeholder="$t('Select')"
            :options="transactionPermissionOptions"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Transaction Permission -->

        <!-- code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="number"
            for="code"
          />
        </b-col>
        <!-- code -->
        <!--Currency  -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>
        <!--Currency  -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BBadge,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import { parentCategory, transactionPermission, parentCategoryList } from '../../../helpers/data'
import { toBalance } from '../../../helpers/formatAmount'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BBadge,
    BRow,
    BCol,
    BSpinner,
    BTable,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      isReadonly: false,
      isView: false,
      isLoadingLevel2Category: false,
      isLoadingLevel3Category: false,
      isLoadingIFRSCategory: false,
      isEditable: false,
      isLoadingBanks: false,
      showFinancialInstitution: false,
      customerInformation: {
        accountNumber: null,
        ledgerBalance: null,
      },
      buttonLabel: this.$t('Save'),
      required,
      parentCategoryList,
      modalTitle: this.$t('Create General Ledger'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'parentCategory', sortable: true, label: 'Account Class' },
        { key: 'parentName', sortable: true, label: 'Parent Name' },
        {
          key: 'ifrsAccountMappingName',
          sortable: true,
          label: 'IFRS Category',
        },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      glFormData: {
        name: '',
        accountCategoryCode: '',
        parentCategory: '',
        ifrsAccountMappingID: '',
        transactionPermission: '',
        level2Category: '',
        bankCode: '',
        currencyCode: '',
      },
      // level1CategoryOptions: parentCategory,
      categoryOptions: [],
      // transactionPermissionOptions: transactionPermission,
      generalLedgersList: [],
      selected: '',
      level2CategoryOptions: [],
      level3CategoryOptions: [],
      ifrsCategoryOptions: [],
      filter: {
        name: '',
        accountCategoryCode: '',
        parentCategory: '',
        transactionPermission: '',
        code: '',
        currencyCode: '',
        status: '',
        sortColumn: '',
        direction: '',
      },
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      bankOptions: 'operations/bankOptions',
    }),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedGeneralLedgersList() {
      return this.getTableCache('general-ledgers', this.cacheKey)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    transactionPermissionOptions() {
      return transactionPermission.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
    level1CategoryOptions() {
      return parentCategory.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
  },
  watch: {
    selected(newVal) {
      this.glFormData.parentCategory = newVal
    },
    transactionPermission(newVal) {
      this.glFormData.transactionPermission = newVal
    },
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchGeneralLedger()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchGeneralLedger()
    },
  },
  mounted() {
    this.serveCacheAndFetchGeneralLedger()
    this.fetchCategoryOptions()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getLevelTwoCategory: 'account/getLevelTwoCategoryList',
      getLevelThreeCategory: 'account/getLevelThreeCategoryList',
      getGeneralLedger: 'account/getGeneralLedgerList',
      updateGeneralLedger: 'account/updateGeneralLedgerAction',
      createGeneralLedger: 'account/createGeneralLedgerAction',
      getAccountCategory: 'account/getCategoryList',
      getGLAccountBalance: 'account/getGLAccountBalance',
      fetchIFRSCategory: 'account/getIFRSCategoryList',
      updateGeneralLedgerStatus: 'account/updateStatus',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),

    async serveCacheAndFetchGeneralLedger() {
      const { cacheKey } = this
      const cache = this.getTableCache('general-ledgers', cacheKey)

      if (cache && cache.data) {
        this.generalLedgersList = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.fetchGeneralLedger(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchGeneralLedger(false)
      }
    },
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({
          permission: accountPermissions.VIEW_GENERAL_LEDGER,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data
            .filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async getIFRSCategory() {
      try {
        const response = await this.fetchIFRSCategory()
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.ifrsCategoryOptions = data.data.map(item => ({
            label: item.description,
            value: item.code,
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchLevelThreeCategory(selectedValue) {
      try {
        this.showFinancialInstitution = selectedValue === 10200000 // "Due From" value code on level 2 category
        this.isLoadingLevel3Category = true
        const response = await this.getLevelThreeCategory(selectedValue)
        this.level3CategoryOptions = response.data.data
          .map(item => ({ label: item.description, value: item.code }))
          .sort((a, b) => a.label.localeCompare(b.label))
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoadingLevel3Category = false
      }
    },
    async fetchLevelTwoCategory(selectedValue) {
      try {
        this.isLoadingLevel2Category = true
        this.isLoadingIFRSCategory = true
        const response = await this.getLevelTwoCategory(selectedValue)

        const parentCategories = parentCategoryList.find(item => item.label === selectedValue)
        const ifrsResponse = await this.fetchIFRSCategory(parentCategories.code)
        this.level2CategoryOptions = response.data.data
          .map(item => ({ label: item.description, value: item.code }))
          .sort((a, b) => a.label.localeCompare(b.label))
        this.ifrsCategoryOptions = ifrsResponse.data.data
          .map(item => ({ label: item.description, value: item.id }))
          .sort((a, b) => a.label.localeCompare(b.label))
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoadingLevel2Category = false
        this.isLoadingIFRSCategory = false
      }
    },

    async fetchCategoryOptions() {
      try {
        const response = this.getAccountCategory()
        const { data, status } = await response
        if (status === 200 && data.data) {
          const options = data.data.map(item => ({
            label: item.description,
            value: item.code,
          }))
          this.categoryOptions = options.sort((a, b) => a.label.localeCompare(b.label))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async glStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active'
        ? accountPermissions.DEACTIVATE_GENERAL_LEDGER
        : accountPermissions.ACTIVATE_GENERAL_LEDGER

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateGeneralLedgerStatus({
            statusValues,
            actionType,
            routeName: 'GLAccount',
            permission,
          })
          if (response.status === 200) {
            this.fetchGeneralLedger(true)
            const requestToText = this.$t('Your request to')
            const ledgerText = this.$t('General Ledger was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${ledgerText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const requestToText = this.$t('Your request to')
          const ledgerText = this.$t('General Ledger failed. Reason:')
          this.alertError({ text: `${requestToText} ${action} ${ledgerText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const ledgerText = this.$t('this General Ledger?')
      this.confirm(updateStatus, { title: '', text: `${areYouSureText} ${action} ${ledgerText}` })
    },
    async generalLedgerAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createGeneralLedger(this.glFormData)
          : await this.updateGeneralLedger({
            id: this.glFormData.id,
            name: this.glFormData.name,
            ifrsAccountMappingID: this.getifrsAccountMapping(
              this.glFormData.ifrsAccountMappingID,
            ),
            transactionPermission: this.glFormData.transactionPermission,
            bankCode: this.glFormData.bankCode,
          })

        const { status } = await response
        if (status === 200) {
          this.fetchGeneralLedger(true)
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const ledgerText = this.$t('General Ledger was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${ledgerText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.glRules.validate().then(async success => {
        if (success) {
          const areYouSureText = this.$t('Are you sure you want to')
          const ledgerText = this.$t('this General Ledger?')
          this.confirm(this.generalLedgerAction, {
            text: `${areYouSureText} ${this.buttonLabel} ${ledgerText}`,
          })
        }
      })
    },

    getifrsAccountMapping(value) {
      const regex = /\d/
      if (regex.test(value)) {
        return this.ifrsCategoryOptions.find(
          item => item.value === this.glFormData.ifrsAccountMappingID,
        ).value
      }
      return this.ifrsCategoryOptions.find(
        item => item.label === this.glFormData.ifrsAccountMappingID,
      ).value
    },
    async fetchGLAccountBalance(items) {
      try {
        this.showSpinner = true
        const response = await this.getGLAccountBalance(items.id)
        const { status, data } = await response
        if (status === 200 && data.data) {
          if (data.balances.length === 0) {
            this.customerInformation.accountNumber = data.data.code
            this.customerInformation.ledgerBalance = '0'
          } else {
            this.customerInformation.accountNumber = data.data.code
            this.customerInformation.ledgerBalance = toBalance(data.consolidatedBalance)
          }
        }
        this.$root.$emit('bv::show::modal', 'view-balance')
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async onSelectGeneralLedgerItem(items, activity, val) {
      this.glFormData.name = items.name
      this.glFormData.parentCategory = items.parentCategory
      this.fetchLevelTwoCategory(items.parentCategory)
      this.glFormData.transactionPermission = items.transactionPermission
      this.glFormData.level2Category = items.level2CategoryName
      this.glFormData.id = items.id
      this.glFormData.code = items.code
      this.glFormData.currencyCode = items.currencyCode
      this.glFormData.ifrsAccountMappingID = items.ifrsAccountMappingName
      this.glFormData.accountCategoryCode = items.parentName
      this.showFinancialInstitution = items.level2Code === '10200000' // "Due From" value code on level 2 category
      this.glFormData.bankCode = items.bankCode
      this.isView = val !== 'edit'
      this.isEditable = true
      this.modalTitle = val === 'edit' ? this.$t('Edit General Ledger') : this.$t('General Ledger')
      this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async fetchGeneralLedger(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        this.hideModal('modal-filter')
        const response = await this.getGeneralLedger({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_GENERAL_LEDGER,
        })

        const { status, data } = response
        if (status === 200 && data.data) {
          const cache = this.cachedGeneralLedgersList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.generalLedgersList = data.data
            this.totalCount = data.recordCount
            // If on page 1 and data changed, mark all general ledgers cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('general-ledgers')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'general-ledgers',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.recordCount,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    getLevel1CategoryID(value) {
      const levelId = parentCategoryList.find(item => item.label === value)
      return levelId.code
    },

    resetModal() {
      this.glFormData = { ...null }
      this.modalTitle = this.$t('Create General Ledger')
      this.buttonLabel = this.$t('Save')
      this.isReadonly = false
      this.isLoading = false
      this.isView = false
      this.isEditable = false
    },
    resetFilters() {
      this.filter = {}
      this.markTableCacheStale('general-ledgers')
    },
    resetFilter() {
      this.filter = {}
      this.markTableCacheStale('general-ledgers')
      this.fetchGeneralLedger(true)
    },
  },
}
</script>
